import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DeletionInfo, Meta, RemoveUserRequest, UserStatistics } from "entities/common";
import { UserItem } from "entities/companies";

type ModalState = {
  items: UserItem[];
  current: UserItem | null;
  statistics: UserStatistics | null;
  isLoading: boolean;
  isCurrentLoading: boolean;
  isStatisticsLoading: boolean;
  isDeletionInfoLoading: boolean;
  deletionInfo: DeletionInfo | null;
  meta: Meta;
  errors: { detail: Record<string, string> } | null;
  successfullyRemovedId: number | null;
};

const initialState: ModalState = {
  items: [],
  current: null,
  statistics: null,
  deletionInfo: null,
  isLoading: false,
  isCurrentLoading: false,
  isStatisticsLoading: false,
  isDeletionInfoLoading: false,
  meta: {
    total: 0,
    page: 1,
    page_size: 10,
    total_pages: 0,
  },
  errors: null,
  successfullyRemovedId: null,
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    getUsersListRequest: {
      reducer(state, action: PayloadAction<string>) {
        void action.payload;
        state.isLoading = true;
      },
      prepare: (query = "") => ({ payload: query }),
    },
    getUsersListSuccess: (state, action: PayloadAction<{ items: UserItem[] } & Meta>) => {
      state.isLoading = false;
      state.items = action.payload.items;
      state.meta = {
        total: action.payload.total,
        page: action.payload.page,
        page_size: action.payload.page_size,
        total_pages: action.payload.total_pages,
      };
    },
    getUsersListFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    getUserStatisticsRequest: (state) => {
      state.isStatisticsLoading = true;
    },
    getUserStatisticsSuccess: (state, action: PayloadAction<UserStatistics>) => {
      state.statistics = action.payload;
      state.isStatisticsLoading = false;
    },
    getUserStatisticsFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isStatisticsLoading = false;
    },

    getUserDeletionInfoRequest: (state, action: PayloadAction<number>) => {
      void action.payload;
      state.isDeletionInfoLoading = true;
    },
    getUserDeletionInfoSuccess: (state, action: PayloadAction<DeletionInfo>) => {
      state.deletionInfo = action.payload;
      state.isDeletionInfoLoading = false;
    },
    getUserDeletionInfoFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isDeletionInfoLoading = false;
    },

    removeUserRequest: (
      state,
      action: PayloadAction<{
        userId: number;
        body: RemoveUserRequest;
      }>,
    ) => {
      void action.payload;
      state.isLoading = true;
      state.successfullyRemovedId = null;
    },
    removeUserSuccess: (state, action: PayloadAction<number>) => {
      state.isLoading = false;
      state.successfullyRemovedId = action.payload;
    },
    removeUserFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    resetSuccessfullyRemovedUser: (state) => {
      state.successfullyRemovedId = null;
    },
    resetErrorsUser: (state) => {
      state.errors = null;
    },
    resetUser: (state) => {
      state.items = [];
      state.current = null;
    },
    resetUserList: (state) => {
      state.items = [];
      state.current = null;
      state.statistics = null;
      state.deletionInfo = null;
      state.meta = {
        total: 0,
        page: 1,
        page_size: 10,
        total_pages: 0,
      };
    },
    changePage: (state, action: PayloadAction<number>) => {
      state.meta.page = action.payload;
    },
  },
});

export const {
  getUsersListRequest,
  getUsersListSuccess,
  getUsersListFailure,
  removeUserRequest,
  removeUserSuccess,
  removeUserFailure,
  resetSuccessfullyRemovedUser,
  resetErrorsUser,
  resetUser,
  changePage,
  getUserStatisticsRequest,
  getUserStatisticsSuccess,
  getUserStatisticsFailure,
  getUserDeletionInfoRequest,
  getUserDeletionInfoSuccess,
  getUserDeletionInfoFailure,
  resetUserList,
} = usersSlice.actions;
export default usersSlice.reducer;
